/**
 * RESIDENT PROFILE MODULE EXPORT
 * 
 * This module provides comprehensive resident profile functionality including:
 * - Personal information management
 * - Medical history and assessments
 * - Medication management with detailed tracking
 * - Care plan development and monitoring
 * - Family and emergency contacts
 * - Document management
 * - Activity and social engagement tracking
 * 
 * Features:
 * 1. Personal Information - Demographics, contact details, preferences
 * 2. Medical History - Diagnoses, allergies, surgical history
 * 3. Medications - Current prescriptions, interactions, compliance
 * 4. Care Plan - Goals, interventions, progress tracking
 * 5. Family & Contacts - Emergency contacts, family members
 * 6. Assessments - Clinical assessments and evaluations
 * 7. Appointments - Scheduled visits and consultations
 * 8. Documents - Medical records and reports
 * 9. Activities - Social engagement and recreational activities
 * 10. Analytics - Health trends and progress reports
 *
 * UI: The former `renderResidentProfile` export was removed; the live modal is `EnhancedResidentProfile` in `Residents.js`.
 */

// Helper methods to generate different data for each resident
const getPrimaryDiagnosis = (residentId) => {
  const diagnoses = {
    1: ['Alzheimer\'s Disease', 'Type 2 Diabetes', 'Osteoarthritis'],
    2: ['Advanced Alzheimer\'s Disease', 'Parkinson\'s Disease', 'Heart Failure'],
    3: ['Hypertension', 'Chronic Kidney Disease', 'Depression'],
    4: ['Prostate Cancer', 'Benign Prostatic Hyperplasia', 'Osteoporosis'],
    5: ['Rheumatoid Arthritis', 'Osteoporosis', 'Anxiety Disorder']
  };
  return diagnoses[residentId] || ['Hypertension', 'Diabetes'];
};

const getSecondaryConditions = (residentId) => {
  const conditions = {
    1: ['Hypertension', 'Osteoporosis', 'Sleep Apnea'],
    2: ['Depression', 'Urinary Incontinence', 'Pressure Ulcers'],
    3: ['Diabetes', 'Heart Disease', 'Arthritis'],
    4: ['Hypertension', 'Diabetes', 'Depression'],
    5: ['Hypertension', 'Depression', 'Osteoarthritis']
  };
  return conditions[residentId] || ['Hypertension'];
};

const getAllergies = (residentId) => {
  const allergies = {
    1: ['Penicillin', 'Shellfish', 'Latex'],
    2: ['Codeine', 'Aspirin', 'Latex'],
    3: ['Sulfa', 'Iodine', 'Latex'],
    4: ['Penicillin', 'Aspirin', 'Latex'],
    5: ['Penicillin', 'Sulfa', 'Latex']
  };
  return allergies[residentId] || ['Penicillin'];
};

const getSurgicalHistory = (residentId) => {
  const surgeries = {
    1: [
      { procedure: 'Hip Replacement', surgeon: 'Dr. James Wilson', date: '2020-03-15', notes: 'Successful recovery' },
      { procedure: 'Cataract Surgery', surgeon: 'Dr. Sarah Chen', date: '2021-08-22', notes: 'Bilateral' }
    ],
    2: [
      { procedure: 'Pacemaker Insertion', surgeon: 'Dr. Emily Wilson', date: '2022-01-10', notes: 'Dual chamber' },
      { procedure: 'Hip Fracture Repair', surgeon: 'Dr. Michael Chen', date: '2021-11-05', notes: 'ORIF procedure' }
    ],
    3: [
      { procedure: 'Gallbladder Removal', surgeon: 'Dr. Robert Smith', date: '2019-06-20', notes: 'Laparoscopic' },
      { procedure: 'Cataract Surgery', surgeon: 'Dr. Sarah Chen', date: '2020-09-15', notes: 'Left eye' }
    ],
    4: [
      { procedure: 'Prostate Surgery', surgeon: 'Dr. David Kim', date: '2021-03-12', notes: 'Robotic assisted' },
      { procedure: 'Knee Replacement', surgeon: 'Dr. James Wilson', date: '2020-07-08', notes: 'Right knee' }
    ],
    5: [
      { procedure: 'Hip Replacement', surgeon: 'Dr. James Wilson', date: '2022-02-14', notes: 'Left hip' },
      { procedure: 'Cataract Surgery', surgeon: 'Dr. Sarah Chen', date: '2021-12-03', notes: 'Both eyes' }
    ]
  };
  return surgeries[residentId] || [];
};

const getMedications = (residentId) => {
  const medications = {
    1: [
      { medication: 'Donepezil 10mg', dosage: '10mg once daily at bedtime', doctor: 'Dr. Robert Smith' },
      { medication: 'Metformin 500mg', dosage: '500mg twice daily', doctor: 'Dr. Robert Smith' },
      { medication: 'Lisinopril 10mg', dosage: '10mg once daily', doctor: 'Dr. Robert Smith' }
    ],
    2: [
      { medication: 'Levodopa 100mg', dosage: '100mg three times daily', doctor: 'Dr. Emily Wilson' },
      { medication: 'Furosemide 40mg', dosage: '40mg once daily', doctor: 'Dr. Emily Wilson' },
      { medication: 'Digoxin 0.25mg', dosage: '0.25mg once daily', doctor: 'Dr. Emily Wilson' }
    ],
    3: [
      { medication: 'Lisinopril 5mg', dosage: '5mg once daily', doctor: 'Dr. Michael Chen' },
      { medication: 'Metformin 1000mg', dosage: '1000mg twice daily', doctor: 'Dr. Michael Chen' },
      { medication: 'Sertraline 50mg', dosage: '50mg once daily', doctor: 'Dr. Michael Chen' }
    ],
    4: [
      { medication: 'Tamsulosin 0.4mg', dosage: '0.4mg once daily', doctor: 'Dr. David Kim' },
      { medication: 'Alendronate 70mg', dosage: '70mg once weekly', doctor: 'Dr. David Kim' },
      { medication: 'Metformin 500mg', dosage: '500mg twice daily', doctor: 'Dr. David Kim' }
    ],
    5: [
      { medication: 'Methotrexate 10mg', dosage: '10mg once weekly', doctor: 'Dr. Lisa Anderson' },
      { medication: 'Alendronate 70mg', dosage: '70mg once weekly', doctor: 'Dr. Lisa Anderson' },
      { medication: 'Sertraline 25mg', dosage: '25mg once daily', doctor: 'Dr. Lisa Anderson' }
    ]
  };
  return medications[residentId] || [];
};

const getAssessments = (residentId) => {
  const assessments = {
    1: [
      {
        id: 'ASS001',
        name: 'Mini-Mental State Examination (MMSE)',
        type: 'Cognitive Assessment',
        date: '2024-01-15',
        assessor: 'Dr. Michael Chen',
        status: 'Completed',
        score: 18,
        maxScore: 30,
        findings: 'Mild cognitive impairment. Score indicates moderate dementia.',
        results: 'Patient shows signs of early-stage Alzheimer\'s disease.',
        recommendations: 'Continue Donepezil therapy, increase social engagement activities.',
        nextAssessment: '2024-03-01'
      },
      {
        id: 'ASS002',
        name: 'Geriatric Depression Scale (GDS)',
        type: 'Psychological Assessment',
        date: '2024-01-10',
        assessor: 'Dr. Sarah Johnson',
        status: 'Completed',
        score: 8,
        maxScore: 15,
        findings: 'Mild depression symptoms present.',
        results: 'Patient experiencing some depressive symptoms but not severe.',
        recommendations: 'Monitor mood, consider counseling if symptoms worsen.',
        nextAssessment: '2024-04-01'
      },
      {
        id: 'ASS003',
        name: 'Delirium Screening (4AT)',
        type: 'Clinical Safety',
        date: '2024-01-12',
        assessor: 'RN Jane Smith',
        status: 'Pending',
        findings: 'Screening in progress.',
        results: 'â€”',
        recommendations: 'Complete attention items.',
        nextAssessment: '2024-01-13'
      }
    ],
    2: [
      {
        id: 'ASS001',
        name: 'Unified Parkinson\'s Disease Rating Scale (UPDRS)',
        type: 'Neurological Assessment',
        date: '2024-01-20',
        assessor: 'Dr. Emily Wilson',
        status: 'Completed',
        score: 45,
        maxScore: 108,
        findings: 'Moderate to severe Parkinson\'s symptoms.',
        results: 'Patient shows significant motor impairment and cognitive decline.',
        recommendations: 'Adjust Levodopa dosage, increase physical therapy.',
        nextAssessment: '2024-02-20'
      }
    ],
    3: [
      {
        id: 'ASS001',
        name: 'Kidney Function Assessment',
        type: 'Medical Assessment',
        date: '2024-01-18',
        assessor: 'Dr. Michael Chen',
        status: 'Completed',
        score: 65,
        maxScore: 100,
        findings: 'Moderate kidney function decline.',
        results: 'eGFR indicates stage 3 chronic kidney disease.',
        recommendations: 'Monitor kidney function, adjust medications as needed.',
        nextAssessment: '2024-03-18'
      }
    ],
    4: [
      {
        id: 'ASS001',
        name: 'Prostate Cancer Monitoring',
        type: 'Oncological Assessment',
        date: '2024-01-12',
        assessor: 'Dr. David Kim',
        status: 'Completed',
        score: 2,
        maxScore: 10,
        findings: 'Stable prostate cancer, no progression.',
        results: 'PSA levels remain stable, no new lesions detected.',
        recommendations: 'Continue current treatment, monitor PSA quarterly.',
        nextAssessment: '2024-04-12'
      }
    ],
    5: [
      {
        id: 'ASS001',
        name: 'Rheumatoid Arthritis Assessment',
        type: 'Rheumatological Assessment',
        date: '2024-01-16',
        assessor: 'Dr. Lisa Anderson',
        status: 'Completed',
        score: 3.2,
        maxScore: 10,
        findings: 'Moderate disease activity.',
        results: 'DAS28 score indicates moderate rheumatoid arthritis activity.',
        recommendations: 'Continue Methotrexate, consider adding biologic if needed.',
        nextAssessment: '2024-03-16'
      }
    ]
  };
  return assessments[residentId] || [];
};

const getAppointments = (residentId) => {
  const appointments = {
    1: [
      {
        id: 'APT001',
        title: 'Medical Review',
        type: 'Routine Checkup',
        date: '2024-02-15',
        time: '10:00 AM',
        duration: 30,
        location: 'Dr. Smith\'s Office',
        status: 'Scheduled',
        notes: 'Regular health checkup and medication review',
        preparation: 'Bring current medications list',
        provider: 'Dr. Robert Smith'
      },
      {
        id: 'APT002',
        title: 'Neurology Consultation',
        type: 'Specialist Visit',
        date: '2024-02-20',
        time: '2:00 PM',
        duration: 45,
        location: 'Neurology Clinic',
        status: 'Scheduled',
        notes: 'Cognitive assessment and dementia care review',
        preparation: 'Complete MMSE questionnaire',
        provider: 'Dr. Michael Chen'
      }
    ],
    2: [
      {
        id: 'APT001',
        title: 'Cardiology Review',
        type: 'Specialist Visit',
        date: '2024-02-18',
        time: '11:00 AM',
        duration: 45,
        location: 'Cardiology Department',
        status: 'Scheduled',
        notes: 'Heart failure monitoring and medication adjustment',
        preparation: 'ECG and blood work required',
        provider: 'Dr. Emily Wilson'
      }
    ],
    3: [
      {
        id: 'APT001',
        title: 'Nephrology Consultation',
        type: 'Specialist Visit',
        date: '2024-02-22',
        time: '9:00 AM',
        duration: 30,
        location: 'Nephrology Clinic',
        status: 'Scheduled',
        notes: 'Chronic kidney disease monitoring',
        preparation: 'Blood and urine tests required',
        provider: 'Dr. Michael Chen'
      }
    ],
    4: [
      {
        id: 'APT001',
        title: 'Oncology Follow-up',
        type: 'Specialist Visit',
        date: '2024-02-25',
        time: '1:00 PM',
        duration: 30,
        location: 'Oncology Department',
        status: 'Scheduled',
        notes: 'Prostate cancer monitoring and PSA review',
        preparation: 'PSA blood test required',
        provider: 'Dr. David Kim'
      }
    ],
    5: [
      {
        id: 'APT001',
        title: 'Rheumatology Review',
        type: 'Specialist Visit',
        date: '2024-02-28',
        time: '10:30 AM',
        duration: 45,
        location: 'Rheumatology Clinic',
        status: 'Scheduled',
        notes: 'Rheumatoid arthritis management and medication review',
        preparation: 'Joint assessment and blood work',
        provider: 'Dr. Lisa Anderson'
      }
    ]
  };
  return appointments[residentId] || [];
};

export const ResidentProfileExportModule = {
  // Generate comprehensive resident profile data for all residents
  generateResidentProfileData: (resident) => {
    const residentId = resident.id;
    const isEleanor = residentId === 1;
    
    return {
      personalInfo: {
        id: resident.id,
        name: resident.name,
        dateOfBirth: resident.dateOfBirth,
        age: resident.age,
        gender:
          resident.gender ||
          resident.demographics?.gender ||
          (residentId % 2 === 0 ? 'Male' : 'Female'),
      room: resident.room,
      facility: resident.facility,
      ward: resident.ward,
      admissionDate: resident.admissionDate,
      careLevel: resident.careLevel,
      mobility: resident.mobility,
      cognitiveStatus: resident.cognitiveStatus,
      bloodType: resident.bloodType,
      weight: resident.weight,
      height: resident.height,
      bmi: resident.weight && resident.height ? (resident.weight / ((resident.height/100) ** 2)).toFixed(1) : null,
      primaryLanguage: resident.demographics?.primaryLanguage || 'English',
      religion: resident.demographics?.religion || 'Not specified',
      maritalStatus: resident.demographics?.maritalStatus || 'Not specified',
      emergencyContact: resident.emergencyContact,
      preferences: {
        dietary: resident.dietaryRestrictions || ['Diabetic Diet', 'Low Sodium'],
        activities: ['Reading', 'Music', 'Gardening'],
        communication: 'Verbal',
        privacy: 'Standard'
      }
    },

    medicalHistory: {
      primaryDiagnosis: resident.primaryDiagnosis || getPrimaryDiagnosis(residentId),
      secondaryDiagnoses: getSecondaryConditions(residentId),
      allergies: resident.allergies || getAllergies(residentId),
      surgicalHistory: getSurgicalHistory(residentId),
      familyHistory: [
        { condition: 'Diabetes', relation: 'Mother', age: 65 },
        { condition: 'Heart Disease', relation: 'Father', age: 72 }
      ],
      immunizations: [
        { vaccine: 'COVID-19', date: '2024-01-15', nextDue: '2025-01-15' },
        { vaccine: 'Influenza', date: '2024-10-01', nextDue: '2025-10-01' }
      ]
    },

    medications: {
      current: getMedications(residentId).map((med, index) => ({
        id: `RX${String(residentId).padStart(2, '0')}${String(index + 1).padStart(2, '0')}`,
        medication: med.medication,
        dosage: med.dosage,
        doctor: med.doctor,
        prescribedBy: med.doctor,
          status: 'Active',
        startDate: '2023-03-20',
        lastRefill: '2024-01-15',
        nextRefill: '2024-02-15',
        nextReview: '2024-03-20',
        repeatsRemaining: 2,
        totalRepeats: 5,
        pbsEligible: true,
        authority: false,
        unitPrice: 12.50,
        category: 'Prescription',
        instructions: 'Take as directed',
        sideEffects: 'May cause mild side effects',
        monitoring: 'Regular monitoring required',
        adherenceRate: 95,
        lastTaken: '2024-01-24 21:00',
        nextDose: '2024-01-25 21:00',
        autoRefill: true,
        reminderEnabled: true,
        interactions: ['May interact with other medications'],
        contraindications: ['Consult doctor if allergic'],
        storage: 'Store at room temperature',
        expiryDate: '2025-01-15',
        frequency: 'Daily',
        schedule: {
          morning: true,
          afternoon: false,
          evening: false,
          bedtime: false
        }
      })),
      discontinued: [
        { 
          medication: 'Ibuprofen 400mg', 
          reason: 'Gastric irritation', 
          date: '2024-10-15',
          doctor: 'Dr. Michael Chen'
        }
      ],
      interactions: [
        { 
          type: 'High Risk', 
          medications: 'Warfarin + Aspirin', 
          description: 'Monitor bleeding risk',
          status: 'critical'
        }
      ],
      compliance: {
        rate: 94,
        daysThisMonth: 28,
        totalDays: 30,
        missedDoses: 2
      }
    },

    carePlan: {
      id: `CP-${resident.id}`,
      status: 'Active',
      lastUpdated: '2024-12-15',
      goals: [
        {
          id: 'goal001',
          category: 'Mobility',
          description: 'Improve walking independence',
          target: 'Walk 50m with minimal assistance',
          progress: 75,
          status: 'In Progress',
          targetDate: '2025-03-01',
          interventions: ['Physiotherapy 3x/week', 'Walking program']
        },
        {
          id: 'goal002',
          category: 'Social',
          description: 'Increase social engagement',
          target: 'Participate in 3 group activities per week',
          progress: 90,
          status: 'On Track',
          targetDate: '2025-02-15',
          interventions: ['Activity program', 'Social groups']
        }
      ],
      interventions: [
        {
          id: 'int001',
          type: 'Physiotherapy',
          frequency: '3x per week',
          provider: 'Sarah Mitchell (PT)',
          status: 'Active',
          lastSession: '2025-01-24',
          nextSession: '2025-01-27'
        }
      ]
    },

    assessments: getAssessments(residentId),

    appointments: getAppointments(residentId),

    family: {
      emergencyContact: resident.emergencyContact,
      familyMembers: resident.supportNetwork?.familyMembers?.length > 0 
        ? resident.supportNetwork.familyMembers.map((member, idx) => ({
            name: member.name,
            relationship: member.relationship,
            phone: member.phone || 'Not provided',
            email: member.email || 'Not provided',
            address: member.address || 'Not provided',
            isPrimary: idx === 0,
            canMakeDecisions: member.relationship === 'Power of Attorney' || member.relationship === 'Guardian'
          }))
        : [
            {
              name: 'Contact Not Set',
              relationship: 'Family Member',
              phone: 'Not configured',
              email: 'Not configured',
              address: 'Not configured',
              isPrimary: true,
              canMakeDecisions: false
            }
          ],
      communicationHistory: resident.supportNetwork?.communicationHistory?.length > 0
        ? resident.supportNetwork.communicationHistory.slice(-5)
        : []
    },

    documents: resident.documents?.length > 0
      ? resident.documents.map((doc, idx) => ({
          id: doc.id || `DOC${String(idx + 1).padStart(3, '0')}`,
          name: doc.name || `Document ${idx + 1}`,
          type: doc.type || 'Document',
          date: doc.date || new Date().toISOString().split('T')[0],
          size: doc.size || '1.0 MB',
          status: doc.status || 'Active',
          uploadedBy: doc.uploadedBy || 'System',
          description: doc.description || 'No description available',
          category: doc.category || 'General'
        }))
      : [
          {
            id: 'DOC001',
            name: 'Comprehensive Care Plan 2024',
            type: 'Care Plan',
            date: '2024-01-15',
            size: '2.4 MB',
            status: 'Active',
            uploadedBy: 'Dr. Sarah Johnson',
            description: 'Complete care plan including medical, social, and psychological needs',
            category: 'Care Planning'
          }
        ],

    activities: {
      scheduled: [
        {
          id: 'ACT001',
          name: 'Music Therapy Session',
          date: '2024-02-05',
          time: '2:00 PM',
          duration: 45,
          location: 'Activity Room A',
          facilitator: 'Jane Smith',
          participants: 8,
          status: 'Scheduled',
          description: 'Group music therapy focusing on memory and social interaction',
          category: 'Therapeutic'
        },
        {
          id: 'ACT002',
          name: 'Art & Craft Workshop',
          date: '2024-02-08',
          time: '10:00 AM',
          duration: 60,
          location: 'Craft Room',
          facilitator: 'Maria Garcia',
          participants: 6,
          status: 'Scheduled',
          description: 'Creative activities to stimulate cognitive function',
          category: 'Creative'
        },
        {
          id: 'ACT003',
          name: 'Gentle Exercise Class',
          date: '2024-02-12',
          time: '11:00 AM',
          duration: 30,
          location: 'Exercise Room',
          facilitator: 'Tom Brown',
          participants: 10,
          status: 'Scheduled',
          description: 'Low-impact exercises for mobility and strength',
          category: 'Physical'
        }
      ],
      completed: [
        {
          id: 'ACT004',
          name: 'Memory Games Group',
          date: '2024-01-30',
          time: '3:00 PM',
          duration: 45,
          location: 'Activity Room B',
          facilitator: 'Sarah Lee',
          participants: 6,
          status: 'Completed',
          notes: 'Excellent participation. Eleanor showed good engagement with word games.',
          category: 'Cognitive',
          rating: 5
        },
        {
          id: 'ACT005',
          name: 'Social Tea Time',
          date: '2024-01-28',
          time: '2:30 PM',
          duration: 60,
          location: 'Common Room',
          facilitator: 'Care Staff',
          participants: 12,
          status: 'Completed',
          notes: 'Good social interaction. Eleanor enjoyed conversation with other residents.',
          category: 'Social',
          rating: 4
        },
        {
          id: 'ACT006',
          name: 'Garden Walk',
          date: '2024-01-25',
          time: '10:30 AM',
          duration: 30,
          location: 'Garden',
          facilitator: 'Volunteer',
          participants: 8,
          status: 'Completed',
          notes: 'Pleasant walk. Eleanor enjoyed the fresh air and nature.',
          category: 'Outdoor',
          rating: 5
        }
      ],
      preferences: ['Music', 'Reading', 'Gardening', 'Social Groups', 'Art & Craft', 'Memory Games'],
      engagement: {
        weeklyAverage: 4.2,
        favoriteActivity: 'Music Therapy',
        participationRate: 85,
        lastActivity: '2024-01-30'
      }
    },

    analytics: {
      healthTrends: {
        weight: [
          { date: '2024-10-01', value: 70 },
          { date: '2024-11-01', value: 69 },
          { date: '2024-12-01', value: 68 },
          { date: '2025-01-01', value: 68 }
        ],
        bloodPressure: [
          { date: '2024-12-01', systolic: 140, diastolic: 85 },
          { date: '2025-01-01', systolic: 135, diastolic: 80 }
        ],
        bloodGlucose: [
          { date: '2024-12-01', value: 7.2 },
          { date: '2024-12-15', value: 6.8 },
          { date: '2025-01-01', value: 6.5 },
          { date: '2025-01-15', value: 6.8 }
        ],
        cognitiveScore: [
          { date: '2024-10-01', value: 20 },
          { date: '2024-11-01', value: 19 },
          { date: '2024-12-01', value: 18 },
          { date: '2025-01-01', value: 18 }
        ]
      },
      engagement: {
        socialActivities: 85,
        medicationCompliance: 94,
        carePlanAdherence: 78,
        activityParticipation: 82,
        familyVisits: 4,
        lastMonthActivities: 12
      },
      healthMetrics: {
        fallRisk: 'Moderate',
        mobilityScore: 6,
        cognitiveScore: 18,
        socialEngagement: 85,
        medicationAdherence: 94,
        overallHealth: 'Stable'
      },
      trends: {
        weightStable: true,
        bloodPressureImproving: true,
        cognitiveDecline: 'Slow',
        socialEngagement: 'High',
        medicationCompliance: 'Excellent'
      }
    }
    };
  }
};

export default ResidentProfileExportModule;
